<template>
  <div class="complex-poly-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$P(z) = (z-z₁)(z-z₂)(z-z₃)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="340" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
           <!-- 坐标轴 -->
           <g transform="translate(300, 200)">
              <line x1="-250" y1="0" x2="250" y2="0" stroke="rgba(255,255,255,0.15)" stroke-width="1.5" />
              <line x1="0" y1="-150" x2="0" y2="150" stroke="rgba(255,255,255,0.15)" stroke-width="1.5" />
              
              <!-- 根的展示与连接线 (形成多边形/骨架) -->
              <polygon :points="rootPolyPoints" fill="rgba(56, 189, 248, 0.05)" stroke="rgba(255,255,255,0.2)" stroke-width="1.5" stroke-dasharray="8,4" />

              <!-- 根 z1, z2, z3 -->
              <g v-for="(root, i) in roots" :key="i">
                <circle :cx="root.re * 30" :cy="-root.im * 30" r="10" :fill="rootColor(i)" stroke="#fff" stroke-width="3" opacity="0.9" />
                <circle :cx="root.re * 30" :cy="-root.im * 30" r="10" :fill="rootColor(i)" stroke="#fff" stroke-width="3" opacity="0.9" />
                <text :x="root.re * 30 + 15" :y="-root.im * 30" fill="#fff" font-size="14" font-weight="900">z{{ i+1 }}</text>
              </g>

              <!-- 韦达定理累加向量链 ( demostrar z1+z2+z3 = -a2 ) -->
              <g v-if="showVieta">
                 <!-- 向量链 -->
                 <line x1="0" y1="0" :x2="roots[0].re * 30" :y2="-roots[0].im * 30" stroke="#3b82f6" stroke-width="2" stroke-dasharray="2,2" />
                 <line :x1="roots[0].re * 30" :y1="-roots[0].im * 30" 
                       :x2="(roots[0].re + roots[1].re) * 30" :y2="-(roots[0].im + roots[1].im) * 30" 
                       stroke="#10b981" stroke-width="2" stroke-dasharray="2,2" />
                 <line :x1="(roots[0].re + roots[1].re) * 30" :y1="-(roots[0].im + roots[1].im) * 30" 
                       :x2="sumRoots.re * 30" :y2="-sumRoots.im * 30" 
                       stroke="#fbbf24" stroke-width="2" stroke-dasharray="2,2" />

                 <!-- 总和向量 (对应 -a2) -->
                 <line x1="0" y1="0" :x2="sumRoots.re * 30" :y2="-sumRoots.im * 30" 
                       stroke="#f43f5e" stroke-width="4" marker-end="url(#arrow-vieta)" />
                 <text :x="sumRoots.re * 30 + 10" :y="-sumRoots.im * 30 - 10" fill="#f43f5e" font-size="14" font-weight="900">Σz = -a₂</text>
              </g>
           </g>

           <defs>
              <marker id="arrow-vieta" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" /></marker>
           </defs>
        </svg>

        <div class="hud-panel compact-hud">
          <div class="hud-item cyan">
            <label>当前多项式 P(z)</label>
            <div class="compact-formula">{{ polynomialString }}</div>
          </div>
          <div class="hud-item orange">
            <label>韦达校验：根之和 Σz₁</label>
            <span class="val">{{ sumRootsDisplay }}i</span>
          </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="root-sliders">
              <div v-for="(root, i) in roots" :key="i" class="root-group">
                 <label>根 z{{ i+1 }}: {{ root.re.toFixed(1) }} + {{ root.im.toFixed(1) }}i</label>
                 <el-slider v-model="root.re" :min="-8" :max="8" :step="0.1" size="small"></el-slider>
                 <el-slider v-model="root.im" :min="-5" :max="5" :step="0.1" size="small"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p>
                <strong>韦达定理：</strong>在复数域中，$n$ 次方程必有 $n$ 个根。
                其根之和为 $z_1+z_2+z_3 = -a_2$；
                两两积之和为 $z_1z_2+z_2z_3+z_3z_1 = a_1$；
                根之积为 $z_1z_2z_3 = -a_0$。
                <strong>观测：</strong>调节根的位置，观察方程各项系数随之呈现的几何对称变化。
              </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'ComplexPolynomialViz',
  data() {
    return {
      showVieta: true,
      roots: [
        { re: 4, im: 3 },
        { re: -3, im: 2 },
        { re: -1, im: -4 }
      ]
    }
  },
  computed: {
    // P(z) = (z-z1)(z-z2)(z-z3)
    // = z^3 - (z1+z2+z3)z^2 + (z1z2+z2z3+z3z1)z - z1z2z3
    sumRoots() {
      return {
        re: this.roots.reduce((acc, r) => acc + r.re, 0),
        im: this.roots.reduce((acc, r) => acc + r.im, 0)
      };
    },
    // (a+bi)(c+di) = (ac-bd) + (ad+bc)i
    prod2() {
       const z1 = this.roots[0], z2 = this.roots[1], z3 = this.roots[2];
       const r12_re = z1.re * z2.re - z1.im * z2.im;
       const r12_im = z1.re * z2.im + z1.im * z2.re;
       const r23_re = z2.re * z3.re - z2.im * z3.im;
       const r23_im = z2.re * z3.im + z2.im * z3.re;
       const r31_re = z3.re * z1.re - z3.im * z1.im;
       const r31_im = z3.re * z1.im + z3.im * z1.re;
       return { re: r12_re + r23_re + r31_re, im: r12_im + r23_im + r31_im };
    },
    prod3() {
       const z1 = this.roots[0], z2 = this.roots[1], z3 = this.roots[2];
       const r12_re = z1.re * z2.re - z1.im * z2.im;
       const r12_im = z1.re * z2.im + z1.im * z2.re;
       return {
          re: r12_re * z3.re - r12_im * z3.im,
          im: r12_re * z3.im + r12_im * z3.re
       };
    },
    a2() { return { re: -this.sumRoots.re, im: -this.sumRoots.im }; },
    a1() { return this.prod2; },
    a0() { return { re: -this.prod3.re, im: -this.prod3.im }; },
    sumRootsDisplay() {
       const s = this.sumRoots;
       return `${s.re.toFixed(1)} ${s.im >= 0 ? '+' : '-'} ${Math.abs(s.im).toFixed(1)}`;
    },
    rootPolyPoints() {
       return this.roots.map(r => `${r.re * 30},${-r.im * 30}`).join(' ');
    },
    polynomialString() {
       const a2 = this.formatCoeff(this.a2, 'z²');
       const a1 = this.formatCoeff(this.a1, 'z');
       const a0 = this.formatCoeff(this.a0, '');
       return `z³ ${a2} ${a1} ${a0}`;
    }
  },
  methods: {
    formatCoeff(c, term) {
       const re = c.re.toFixed(1);
       const im = c.im.toFixed(1);
       if (c.re === 0 && c.im === 0) return "";
       return `${c.re >= 0 ? '+' : ''}(${re}${c.im >= 0 ? '+' : ''}${im}i)${term}`;
    },
    rootColor(i) {
       const colors = ['#3b82f6', '#10b981', '#fbbf24'];
       return colors[i];
    }
  }
}
</script>

<style scoped lang="scss">
.complex-poly-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 6px; .brief { font-size: 13px; color: #1e293b; font-weight: 700; } .formula-chip { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 800; font-family: monospace; font-size: 0.8rem; } }
.simulation-stage { min-height: 340px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; max-width: 180px; pointer-events: none; .hud-item { background: rgba(15, 23, 42, 0.9); border-radius: 8px; border: 1px solid #334155; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 800; margin-bottom: 2px; } .val { font-size: 12px; color: #f8fafc; font-weight: 900; font-family: monospace; } .compact-formula { font-size: 11px; color: #3b82f6; font-weight: 900; font-family: monospace; white-space: normal; line-height: 1.3; } &.cyan { border-left: 3px solid #3b82f6; } &.orange { border-left: 3px solid #f43f5e; } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #fff; padding: 12px 18px; border-radius: 12px; border: 1px solid #e2e8f0; .root-sliders { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; .root-group { label { font-size: 10px; color: #475569; font-weight: 900; margin-bottom: 2px; display: block; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 11px; color: #334155; line-height: 1.5; strong { color: #0f172a; font-weight: 900; } } } }
</style>
